import { ServiceLogo } from "../ui/service-logo.tsx";
import { ArrowsClockwiseIcon, GoogleLogoIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { authApi, q } from "../api.ts";
import { AuthArrow, AuthFrame } from "../auth-frame.tsx";
import { safeNext } from "../next-path.ts";
import { DETAIL_LABELS, reasonText } from "../labels.ts";
import { Button, ErrorText, StageSteps } from "../ui.tsx";

const D = DETAIL_LABELS.auth;

export function SignIn() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const config = useQuery(q.authConfig);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(() => {
    const code = new URLSearchParams(window.location.search).get("error");
    if (!code) return null;
    const reason = code === "AUTH_INVITE_REQUIRED" ? "auth_invite_required"
      : code === "AUTH_EMAIL_NOT_VERIFIED" || code === "account_not_linked" ? "auth_email_not_verified" : "auth_request_failed";
    return new Error(reasonText({ code: reason }));
  });
  const [devCode, setDevCode] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  async function social(provider: "google" | "github") {
    setBusy(true);
    setError(null);
    try { await authApi.social(provider); }
    catch (err) { setError(err); setBusy(false); }
  }

  async function send(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    setResent(false);
    try {
      await authApi.sendCode(email.trim());
      setResent(step === "code");
      setCode("");
      setStep("code");
      if (config.data?.dev_mailbox) {
        const box = await authApi.devMailbox(email.trim()).catch(() => null);
        // The development outbox lists the newest message first.
        const latest = box?.messages.find((message) => /\d{6}/.test(message.subject));
        setDevCode(/(\d{6})/.exec(latest?.subject ?? "")?.[1] ?? null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await authApi.verify(email.trim(), code.trim());
      // An agent's authorization was waiting for this sign-in: continue it (usually to the consent page).
      if (next) { window.location.assign(next); return; }
      // A new session may belong to a different person. Never carry the
      // previous session's cached workspace into it.
      client.clear();
      // A link that needed a session (an invitation, say) continues where it was.
      const back = safeNext(new URLSearchParams(window.location.search).get("next"), window.location.origin);
      if (back) navigate({ to: back });
      else navigate({ to: "/" });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame>
        <h1>{step === "email" ? D.signIn : D.checkEmail}</h1>
        <p className="z-muted">{step === "email" ? D.intro : D.sent(email.trim())}</p>
        <StageSteps steps={[
          { label: D.emailStep, status: step === "email" ? "current" : "complete" },
          { label: D.codeStep, status: step === "code" ? "current" : "upcoming" },
        ]} />
        {step === "email" ? (
          <form onSubmit={send} className="z-flow">
            <div className="z-field">
              <label htmlFor="email">{D.email}</label>
              <input id="email" className="z-input" type="email" autoComplete="email" required disabled={busy} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={D.placeholder} aria-describedby="email-help" />
              <span id="email-help" className="z-help">{D.emailHelp}</span>
            </div>
            <Button tone="primary" type="submit" busy={busy}>
              {D.send} <AuthArrow />
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="z-flow">
            <div className="z-field">
              <label htmlFor="code">{D.code}</label>
              <input
                id="code"
                className="z-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                disabled={busy}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                aria-describedby="code-help"
              />
              <span id="code-help" className="z-help">{D.codeHelp}</span>
              {devCode && <span className="z-help">{D.developmentCode}: <code>{devCode}</code></span>}
            </div>
            <Button tone="primary" type="submit" busy={busy}>
              {D.verify} <AuthArrow />
            </Button>
            <div className="z-inline">
              <Button tone="ghost" size="sm" icon={<ArrowsClockwiseIcon />} disabled={busy} onClick={() => send()}>{D.resend}</Button>
              <Button tone="ghost" size="sm" icon={<PencilSimpleIcon />} disabled={busy} onClick={() => { setStep("email"); setCode(""); setDevCode(null); setResent(false); setError(null); }}>{D.change}</Button>
            </div>
            {resent && <p role="status" className="z-small">{D.resent}</p>}
          </form>
        )}
        <ErrorText error={error} />
        {(config.data?.github || config.data?.google) && step === "email" && (
          <>
            <div className="z-divider">{D.social}</div>
            <div className="z-flow">
              {config.data.github && (
                <Button disabled={busy} onClick={() => void social("github")}>
                  <ServiceLogo service="github" /> {D.github}
                </Button>
              )}
              {config.data.google && (
                <Button disabled={busy} onClick={() => void social("google")}>
                  <GoogleLogoIcon size={16} /> {D.google}
                </Button>
              )}
            </div>
          </>
        )}
    </AuthFrame>
  );
}
